import { faDownload } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type {
	StatementActivityModel,
	StatementTransactionModel,
} from '@supply-mover-2-0/shared';
import {
	STATEMENT_ADJUSTMENT_KIND_LABELS,
	statementOpeningBalanceLabel,
	statementPaymentLineLabel,
} from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { MobilePageHeader } from '../components/layout/MobilePageHeader';
import { DetailPageSkeleton } from '../components/skeletons';
import { useDisplaySettings } from '../hooks/useDisplaySettings';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { saveOrShareBlob } from '../lib/downloadFile';
import { formatCurrency } from '../lib/formatCurrency';
import { formatStatementDate } from '../lib/formatStatementDate';
import { PAGE_CONTENT } from '../lib/pageWidth';
import * as portalStatementService from '../services/portalStatementService';
import { useLoginModalStore } from '../stores/loginModalStore';

const TRANSACTION_TYPE_LABELS: Record<
	StatementTransactionModel['type'],
	string
> = {
	INVOICE: 'Invoice',
	PAYMENT: 'Payment',
	CREDIT: 'Credit Memo',
	ADJUSTMENT: 'Adjustment',
};

/**
 * Statement detail (PD-14046) — renders the stored snapshot with a
 * portal-native, mobile-first layout (transaction cards instead of the CRM's
 * wide tables) and a Download button that streams the snapshot-rendered PDF.
 */
export function StatementDetailPage(): JSX.Element {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const { token, isAuthenticated } = usePortalAuth();
	const display = useDisplaySettings();
	const openLoginModal = useLoginModalStore((s) => s.open);
	const [isDownloading, setIsDownloading] = useState(false);

	const statementId = id != null ? parseInt(id, 10) : NaN;

	const { data, isLoading } = useQuery({
		queryKey: ['portal-statement', statementId],
		queryFn: () =>
			portalStatementService.getStatementDetail(token ?? '', statementId),
		enabled: token != null && !Number.isNaN(statementId),
		staleTime: 60_000,
	});

	const record = data?.data ?? null;
	const snapshot = record?.snapshotJson ?? null;

	const handleDownload = useCallback(async () => {
		if (record == null || token == null) {
			return;
		}
		setIsDownloading(true);
		try {
			const blob = await portalStatementService.downloadStatementPdf(
				token,
				record.id,
			);
			// Browser: normal download. Native shell: write to cache +
			// share sheet — the anchor click silently no-ops in the
			// WebView (PD-14606).
			await saveOrShareBlob(blob, `${record.statementNumber}.pdf`);
		} catch (err) {
			console.error('Error downloading statement PDF:', err);
		} finally {
			setIsDownloading(false);
		}
	}, [record, token]);

	useEffect(() => {
		if (!isAuthenticated) {
			openLoginModal();
		}
	}, [isAuthenticated, openLoginModal]);

	if (!isAuthenticated) {
		return <div />;
	}

	return (
		<div className={`px-4 py-4 ${PAGE_CONTENT}`}>
			<MobilePageHeader
				title={record?.statementNumber ?? 'Statement'}
				backPath="/statements"
			/>
			<div className="flex items-center justify-between mb-4">
				<h1 className="hidden md:block text-lg md:text-xl font-bold text-default">
					{record != null ? t(record.statementNumber) : t('Statement')}
				</h1>
				{record != null && (
					<button
						type="button"
						onClick={() => {
							void handleDownload();
						}}
						disabled={isDownloading}
						className="ml-auto px-4 py-2 text-sm bg-portal-accent hover:bg-portal-accent-hover text-white font-medium rounded-lg min-h-touch"
					>
						<FontAwesomeIcon icon={faDownload} className="mr-2" />
						{isDownloading ? t('Preparing...') : t('Download PDF')}
					</button>
				)}
			</div>

			{isLoading ? (
				/* Skeleton shaped like the statement detail (PD-14692) */
				<DetailPageSkeleton />
			) : snapshot == null ? (
				<div className="flex items-center justify-center py-16 text-brand-muted">
					{t('Statement not found')}
				</div>
			) : (
				<div className="flex flex-col gap-4">
					{/* Parties */}
					<div className="grid grid-cols-2 gap-3">
						<div className="bg-brand-surface border border-brand-border rounded-xl p-3">
							<div className="text-xs uppercase tracking-wide text-brand-muted mb-1">
								{t('Remit To')}
							</div>
							<div className="text-sm font-medium text-default">
								{t(snapshot.remitTo.name)}
							</div>
							{snapshot.remitTo.addressLines.map((line, i) => (
								<div key={i} className="text-xs text-brand-muted">
									{t(line)}
								</div>
							))}
						</div>
						<div className="bg-brand-surface border border-brand-border rounded-xl p-3">
							<div className="text-xs uppercase tracking-wide text-brand-muted mb-1">
								{t('Bill To')}
							</div>
							<div className="text-sm font-medium text-default">
								{t(snapshot.billTo.name)}
							</div>
							{snapshot.billTo.addressLines.map((line, i) => (
								<div key={i} className="text-xs text-brand-muted">
									{t(line)}
								</div>
							))}
						</div>
					</div>

					{/* Meta */}
					<div className="grid grid-cols-2 md:grid-cols-3 gap-3">
						{[
							{
								label: 'Statement Date',
								value: formatStatementDate(snapshot.statementDate),
							},
							// PD-14898 — storefronts whose terms are negotiated
							// per-customer treat them as internal, so the tile drops
							// out entirely rather than rendering a placeholder.
							...(display.showPaymentTerms
								? [
										{
											label: 'Payment Terms',
											value: snapshot.paymentTerms ?? '-',
										},
									]
								: []),
							{
								label: 'Total Balance Due',
								value: formatCurrency(snapshot.totalBalance) ?? '-',
							},
						].map((meta) => (
							<div
								key={meta.label}
								className="bg-brand-surface border border-brand-border rounded-xl p-3"
							>
								<div className="text-xs text-brand-muted">{t(meta.label)}</div>
								<div className="text-sm font-semibold text-default">
									{t(meta.value)}
								</div>
							</div>
						))}
					</div>

					{/* PD-16379 — the period reconciled, then its invoices and
					    payments. Statements stored before the activity block keep
					    their monthly cards (without the running balance, which
					    could disagree with the balance due). */}
					{snapshot.activity != null ? (
						<StatementActivitySections activity={snapshot.activity} />
					) : snapshot.months.length === 0 ? (
						<div className="bg-brand-surface border border-brand-border rounded-xl p-4 text-sm text-brand-muted">
							{t('No activity in the statement period.')}
						</div>
					) : (
						snapshot.months.map((month) => (
							<div key={month.key}>
								<div className="font-semibold text-default mb-2">
									{t(month.label)}
								</div>
								<div className="flex flex-col gap-2">
									{month.transactions.map((txn, index) => {
										const isCharge =
											txn.type === 'INVOICE' || txn.type === 'CREDIT';
										return (
											<div
												key={`${month.key}-${index}`}
												className="bg-brand-surface border border-brand-border rounded-xl p-3 flex items-start justify-between gap-3"
											>
												<div className="min-w-0">
													<div className="text-sm font-medium text-default">
														{t(TRANSACTION_TYPE_LABELS[txn.type])}{' '}
														{t(txn.number !== '' ? txn.number : '')}
													</div>
													<div className="text-xs text-brand-muted">
														{formatStatementDate(txn.date)}
														{txn.dueDate != null && txn.dueDate !== '' && (
															<>
																{' · '}
																{t('Due')} {formatStatementDate(txn.dueDate)}
															</>
														)}
													</div>
												</div>
												<div className="text-right shrink-0">
													<div
														className={`text-sm font-semibold ${
															isCharge ? 'text-default' : 'text-success'
														}`}
													>
														{isCharge
															? t(formatCurrency(txn.charge) ?? '-')
															: t(`-${formatCurrency(txn.payment) ?? ''}`)}
													</div>
												</div>
											</div>
										);
									})}
								</div>
							</div>
						))
					)}

					{/* Aging summary */}
					<div>
						<div className="font-semibold text-default mb-2">
							{t('Aging Summary')}
						</div>
						<div className="grid grid-cols-2 md:grid-cols-3 gap-3">
							{[
								{ label: 'Current', value: snapshot.aging.current },
								{ label: '1 - 30 Days', value: snapshot.aging.days1to30 },
								{ label: '31 - 60 Days', value: snapshot.aging.days31to60 },
								{ label: '61 - 90 Days', value: snapshot.aging.days61to90 },
								{ label: 'Over 90 Days', value: snapshot.aging.days90plus },
								{ label: 'Total Due', value: snapshot.aging.total },
							].map((cell) => (
								<div
									key={cell.label}
									className="bg-brand-surface border border-brand-border rounded-xl p-3"
								>
									<div className="text-xs text-brand-muted">
										{t(cell.label)}
									</div>
									<div className="text-sm font-semibold text-default">
										{formatCurrency(cell.value)}
									</div>
								</div>
							))}
						</div>
						{snapshot.activity != null &&
							snapshot.activity.availableAccountCredit > 0 && (
								<p className="mt-2 text-xs text-brand-muted">
									{t('Available account credit')}
									{t(':')}{' '}
									{t(
										formatCurrency(snapshot.activity.availableAccountCredit) ??
											'-',
									)}{' '}
									{t('(not yet applied to an invoice)')}
								</p>
							)}
					</div>
				</div>
			)}
		</div>
	);
}

/**
 * PD-16379 — the statement period on the storefront: the reconciliation
 * (rows signed so they add up to the closing balance, which is the total
 * due), then the period's invoices and payments as mobile-first cards.
 */
function StatementActivitySections({
	activity,
}: {
	activity: StatementActivityModel;
}): JSX.Element {
	const { t } = useTranslation();
	const summaryRows: Array<{ label: string; value: number }> = [
		{
			label: statementOpeningBalanceLabel(activity),
			value: activity.openingBalance,
		},
		{ label: 'Invoices & charges', value: activity.invoicesAndCharges },
		{
			label: 'Payments & credits',
			value:
				activity.paymentsAndCredits === 0 ? 0 : -activity.paymentsAndCredits,
		},
		...(activity.adjustmentItems.length > 0
			? [{ label: 'Adjustments', value: activity.adjustments }]
			: []),
	];

	return (
		<>
			<div className="bg-brand-surface border border-brand-border rounded-xl p-3">
				<div className="font-semibold text-default mb-2">
					{t('Account Summary')}
				</div>
				{summaryRows.map((row) => (
					<div
						key={row.label}
						className="flex items-center justify-between py-1 text-sm text-default"
					>
						<span>{t(row.label)}</span>
						<span>{t(formatCurrency(row.value) ?? '-')}</span>
					</div>
				))}
				<div className="flex items-center justify-between pt-2 mt-1 border-t border-brand-border text-sm font-semibold text-default">
					<span>{t('Closing balance (total balance due)')}</span>
					<span>{t(formatCurrency(activity.closingBalance) ?? '-')}</span>
				</div>
			</div>

			<div>
				<div className="font-semibold text-default mb-2">
					{t('Invoices this period')}
				</div>
				{activity.invoices.length === 0 ? (
					<div className="bg-brand-surface border border-brand-border rounded-xl p-4 text-sm text-brand-muted">
						{t('No invoices in this period.')}
					</div>
				) : (
					<div className="flex flex-col gap-2">
						{activity.invoices.map((inv) => (
							<div
								key={inv.invoiceId}
								className="bg-brand-surface border border-brand-border rounded-xl p-3 flex items-start justify-between gap-3"
							>
								<div className="min-w-0">
									<div className="text-sm font-medium text-default">
										{t(inv.isCreditMemo ? 'Credit Memo' : 'Invoice')}{' '}
										{t(inv.number)}
									</div>
									<div className="text-xs text-brand-muted">
										{t(formatStatementDate(inv.date))}
										{inv.dueDate != null && (
											<>
												{t(' · ')}
												{t('Due')} {t(formatStatementDate(inv.dueDate))}
											</>
										)}
									</div>
									{inv.billedToName != null && (
										<div className="text-xs text-brand-muted">
											{t('Billed to')}{' '}
											{t(
												inv.billedToName !== ''
													? inv.billedToName
													: 'parent account',
											)}
										</div>
									)}
								</div>
								<div className="text-right shrink-0">
									<div className="text-sm font-semibold text-default">
										{t(formatCurrency(inv.originalAmount) ?? '-')}
									</div>
									{inv.billedToName == null && (
										<div className="text-xs text-brand-muted">
											{t('Open at period end')}{' '}
											{t(formatCurrency(inv.balanceAtPeriodEnd) ?? '-')}
										</div>
									)}
								</div>
							</div>
						))}
					</div>
				)}
			</div>

			<div>
				<div className="font-semibold text-default mb-2">
					{t('Payments & credits this period')}
				</div>
				{activity.payments.length === 0 ? (
					<div className="bg-brand-surface border border-brand-border rounded-xl p-4 text-sm text-brand-muted">
						{t('No payments or credits in this period.')}
					</div>
				) : (
					<div className="flex flex-col gap-2">
						{activity.payments.map((line) => (
							<div
								key={`${line.kind}-${line.sourceId}`}
								className="bg-brand-surface border border-brand-border rounded-xl p-3"
							>
								<div className="flex items-start justify-between gap-3">
									<div className="min-w-0">
										<div className="text-sm font-medium text-default">
											{t(statementPaymentLineLabel(line))}
										</div>
										<div className="text-xs text-brand-muted">
											{t(formatStatementDate(line.date))}
											{line.method != null && (
												<>
													{t(' · ')}
													{t(line.method)}
												</>
											)}
											{line.reference != null && (
												<>
													{t(' · ')}
													{t(line.reference)}
												</>
											)}
										</div>
									</div>
									<div className="text-right shrink-0">
										<div className="text-sm font-semibold text-default">
											{t(formatCurrency(line.amount) ?? '-')}
										</div>
										{line.fee !== 0 && (
											<div className="text-xs text-brand-muted">
												{t('Fee')} {t(formatCurrency(line.fee) ?? '-')}
											</div>
										)}
									</div>
								</div>
								{line.applications.map((app) => (
									<div
										key={app.invoiceId}
										className="flex items-center justify-between mt-1 text-xs text-brand-muted"
									>
										<span>
											{t('Applied to')} {t(app.number)}
										</span>
										<span>{t(formatCurrency(app.amount) ?? '-')}</span>
									</div>
								))}
								{line.kind !== 'ACCOUNT_CREDIT' &&
									line.depositedToAccount !== 0 && (
										<div className="flex items-center justify-between mt-1 text-xs text-brand-muted">
											<span>
												{t(
													line.depositedToAccount > 0
														? 'Deposited to account'
														: 'Taken from account credit',
												)}
											</span>
											<span>
												{t(
													formatCurrency(Math.abs(line.depositedToAccount)) ??
														'-',
												)}
											</span>
										</div>
									)}
							</div>
						))}
					</div>
				)}
			</div>

			{activity.adjustmentItems.length > 0 && (
				<div>
					<div className="font-semibold text-default mb-2">
						{t('Adjustments')}
					</div>
					<div className="flex flex-col gap-2">
						{activity.adjustmentItems.map((item) => (
							<div
								key={`${item.kind}-${item.invoiceId}`}
								className="bg-brand-surface border border-brand-border rounded-xl p-3 flex items-start justify-between gap-3"
							>
								<div className="min-w-0">
									<div className="text-sm font-medium text-default">
										{t(item.number)}
									</div>
									<div className="text-xs text-brand-muted">
										{t(STATEMENT_ADJUSTMENT_KIND_LABELS[item.kind])}
									</div>
									<div className="text-xs text-brand-muted">
										{t(formatCurrency(item.balanceBefore) ?? '-')}
										{t(' → ')}
										{t(formatCurrency(item.balanceAfter) ?? '-')}
									</div>
								</div>
								<div className="text-sm font-semibold text-default shrink-0">
									{t(formatCurrency(item.amount) ?? '-')}
								</div>
							</div>
						))}
					</div>
				</div>
			)}
		</>
	);
}
